import Typography from '@/common/components/base/typography/Typography';
import { animate, motion, useMotionValue, useSpring } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { Box } from 'styled-system/jsx';

const ITEM_HEIGHT = 30;
const VISIBLE_COUNT = 7;
const PADDING_COUNT = Math.floor(VISIBLE_COUNT / 2);

interface TimeSwipeWheelProps {
  values: string[];
  value: string;
  onChange: (val: string) => void;
  label?: string;
  infinite?: boolean;
  className?: string;
}

const TimeSwipeWheel = ({
  values,
  value,
  onChange,
  label,
  infinite = true,
}: TimeSwipeWheelProps) => {
  const y = useMotionValue(0);
  const springY = useSpring(y, { stiffness: 300, damping: 30 });
  const containerRef = useRef<HTMLDivElement>(null);
  const constraintsRef = useRef<HTMLDivElement>(null);

  const currentIndex = values.indexOf(value);

  // Keep track of offset for infinite scroll
  const offset = useRef(0);

  // Generate extended list for seamless infinite scroll
  const extendedValues = infinite
    ? [
        ...values.slice(-PADDING_COUNT),
        ...values,
        ...values,
        ...values.slice(0, PADDING_COUNT),
      ]
    : values;

  const initialOffset = infinite ? PADDING_COUNT + values.length : 0;
  offset.current = initialOffset;

  const snapTo = (offsetY: number) => {
    const rawIndex = Math.round(offsetY / ITEM_HEIGHT);
    let targetIndex = rawIndex - initialOffset + PADDING_COUNT;

    if (infinite) {
      // Normalize index
      targetIndex =
        ((targetIndex % values.length) + values.length) % values.length;
    } else {
      targetIndex = Math.max(0, Math.min(targetIndex, values.length - 1));
    }

    const clampedOffset = initialOffset + targetIndex - PADDING_COUNT;
    const finalY = -clampedOffset * ITEM_HEIGHT;

    animate(springY, finalY, {
      type: 'spring',
      stiffness: 300,
      damping: 30,
    });

    onChange(values[targetIndex]);
  };

  // Initial position
  useEffect(() => {
    const targetY =
      -(initialOffset + currentIndex - PADDING_COUNT) * ITEM_HEIGHT;
    springY.set(targetY);
  }, [currentIndex, springY, initialOffset, label]);

  // Handle drag end
  const onDragEnd = () => {
    const currentY = springY.get();
    snapTo(-currentY);
  };

  // Prevent page scroll when interacting
  useEffect(() => {
    const preventScroll = (e: TouchEvent) => {
      if (containerRef.current?.contains(e.target as Node)) {
        e.preventDefault();
      }
    };

    document.addEventListener('touchmove', preventScroll, { passive: false });
    return () => document.removeEventListener('touchmove', preventScroll);
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
      userSelect="none"
    >
      {label && (
        <Box mb="5" fontWeight="medium" fontSize="xl" color="black-1">
          {label}
        </Box>
      )}

      <Box
        ref={containerRef}
        h={`${ITEM_HEIGHT * VISIBLE_COUNT}px`}
        w="100px"
        position="relative"
        overflow="hidden"
        textAlign="center"
        style={{ touchAction: 'pan-y' }} // crucial for smooth dragging
      >
        {/* Top & bottom gradient overlays */}
        <Box
          pointerEvents="none"
          position="absolute"
          top={0}
          left={0}
          right={0}
          h="60px"
          bg="linear-gradient(to bottom, white 0%, transparent 100%)"
          zIndex={10}
        />
        <Box
          pointerEvents="none"
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          h="60px"
          bg="linear-gradient(to top, white 0%, transparent 100%)"
          zIndex={10}
        />

        {/* Invisible constraints for drag limits (only if not infinite) */}
        {!infinite && (
          <Box
            ref={constraintsRef}
            position="absolute"
            top={`${-PADDING_COUNT * ITEM_HEIGHT}px`}
            h={`${(values.length + PADDING_COUNT * 2) * ITEM_HEIGHT}px`}
            w="100%"
          />
        )}

        <motion.div
          drag="y"
          dragElastic={infinite ? 0.2 : 0.1}
          dragConstraints={infinite ? false : constraintsRef}
          dragTransition={{ bounceStiffness: 300, bounceDamping: 30 }}
          onDragEnd={onDragEnd}
          style={{
            y: springY,
            cursor: 'grab',
          }}
          whileTap={{ cursor: 'grabbing' }}
        >
          {extendedValues.map((v, i) => {
            const relativePos = i - (initialOffset + currentIndex);
            const distance = Math.abs(relativePos);
            const isCenter = distance === 0;
            const opacity = Math.max(0.3, 1 - distance * 0.25);
            const scale = isCenter ? 1.1 : 0.9 + distance * 0.05;
            const fontWeight = isCenter ? 'bold' : 'normal';
            const fontSize = isCenter ? 'x-large' : 'xl';

            return (
              <Typography
                key={`${i}-${v}`}
                h={`${ITEM_HEIGHT}px`}
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize={fontSize}
                fontWeight={fontWeight}
                color="black-1"
                opacity={opacity}
                transform={`scale(${scale})`}
                transition="all 0.2s ease"
                style={{
                  transformOrigin: 'center center',
                }}
              >
                {v}
              </Typography>
            );
          })}
        </motion.div>
      </Box>
    </Box>
  );
};

export default TimeSwipeWheel;
